/**
 * Pool API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  Pool,
  PoolListResponse,
  PoolResponse,
  PoolCreateInput,
  PoolUpdateInput,
} from '../types';

interface ListPoolsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
}

class PoolService {
  /**
   * List pools with pagination.
   * Converts page/size to skip/limit for backend API.
   */
  async listPools(
    workspaceId: string,
    idToken: string,
    options: ListPoolsOptions = {}
  ): Promise<PoolListResponse> {
    const { page = 1, size = 10, sortBy = 'created_at', order = 'desc', search } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PoolListResponse>(`/pools`, {
      params: { skip, limit: size, sort_by: sortBy, order, search: search || undefined },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list pools');
    }

    return response.data;
  }

  /**
   * Get a single pool by ID.
   */
  async getPool(poolId: string, workspaceId: string, idToken: string): Promise<Pool> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PoolResponse>(`/pools/${poolId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get pool');
    }

    return response.data.data;
  }

  /**
   * Create a new pool.
   */
  async createPool(workspaceId: string, idToken: string, data: PoolCreateInput): Promise<Pool> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<PoolResponse>(`/pools`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create pool');
    }

    return response.data.data;
  }

  /**
   * Update a pool.
   */
  async updatePool(
    poolId: string,
    workspaceId: string,
    idToken: string,
    data: PoolUpdateInput
  ): Promise<Pool> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<PoolResponse>(`/pools/${poolId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update pool');
    }

    return response.data.data;
  }

  /**
   * Delete a pool.
   */
  async deletePool(poolId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/pools/${poolId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const poolService = new PoolService();
export default poolService;
